import React, { useState, useEffect } from 'react';
import { avatarStyles, avatarImage, avatarInitials } from '@/styles/avatars';
import { Modal } from '@/components/Modal';
import { useNameFormat } from '@/utils/nameFormat';

interface StudentAvatarProps {
  // Student info (provide either firstName/lastName OR name)
  firstName?: string;
  lastName?: string;
  name?: string; // Alternative: full name to parse

  // Photo sources (photoFileId takes priority)
  photoFileId?: string | null;
  photoUrl?: string | null;

  // Styling
  size?: 'small' | 'medium' | 'mediumLarge' | 'large' | 'responsive';
  border?: boolean;

  // Photo Modal (built-in, just enable it!)
  enablePhotoModal?: boolean; // Automatically adds click + hover + modal

  // Custom interactivity (if you need something other than photo modal)
  onClick?: () => void;
  hoverEffect?: boolean;

  // Accessibility
  alt?: string;
}

export function StudentAvatar({
  firstName,
  lastName,
  name,
  photoFileId,
  photoUrl,
  size = 'medium',
  border = false,
  enablePhotoModal = false,
  onClick,
  hoverEffect = false,
  alt,
}: StudentAvatarProps) {
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [thumbError, setThumbError] = useState(false);
  const [fullError, setFullError] = useState(false);
  const { formatStudentName } = useNameFormat();

  // Reset error states when photo sources change
  useEffect(() => {
    setThumbError(false);
    setFullError(false);
  }, [photoFileId, photoUrl]);

  // Determine photo sources (photoFileId takes priority)
  // Thumbnails (~2-3KB) for avatar display, full image for modal
  const fullPhotoSrc = photoFileId ? `/api/files/${photoFileId}` : photoUrl || null;
  const thumbPhotoSrc = photoFileId ? `/api/files/${photoFileId}?thumb=1` : photoUrl || null;

  // Calculate initials (safe for empty strings)
  const initials =
    firstName && lastName
      ? `${firstName?.[0] || ''}${lastName?.[0] || ''}`
      : name
        ? name
            .split(' ')
            .map((n) => n?.[0] || '')
            .join('')
            .substring(0, 2)
            .toUpperCase()
        : '??';

  // Student display name (uses configured name format)
  const displayName = firstName && lastName ? formatStudentName(firstName, lastName) : name || 'Student';

  // Determine if clickable (photo modal or custom onClick)
  // Thumb error doesn't prevent clicking — user can still try the full photo
  const isClickable = (enablePhotoModal && fullPhotoSrc) || onClick;
  const showHoverEffect = hoverEffect || (enablePhotoModal && fullPhotoSrc);

  // Handle click - photo modal takes priority
  const handleClick = () => {
    if (enablePhotoModal && fullPhotoSrc && !fullError) {
      setPhotoModalOpen(true);
    } else if (onClick) {
      onClick();
    }
  };

  // Size and initials styles
  const sizeStyle = {
    small: { ...avatarStyles.small, width: '40px', height: '40px' },
    medium: avatarStyles.medium,
    mediumLarge: avatarStyles.mediumLarge,
    large: avatarStyles.large,
    responsive: avatarStyles.circular,
  }[size];

  const initialsStyle = {
    small: avatarInitials.small,
    medium: avatarInitials.default,
    mediumLarge: avatarInitials.default,
    large: avatarInitials.large,
    responsive: avatarInitials.default,
  }[size];

  const containerStyle: React.CSSProperties = {
    ...sizeStyle,
    ...(border && { border: '2px solid var(--border-color)' }),
    ...(isClickable && { cursor: 'pointer' }),
    ...(showHoverEffect && { transition: 'transform 0.2s, box-shadow 0.2s' }),
  };

  const handleMouseEnter = showHoverEffect
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
      }
    : undefined;

  const handleMouseLeave = showHoverEffect
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = 'none';
      }
    : undefined;

  return (
    <>
      <div
        style={containerStyle}
        onClick={isClickable ? handleClick : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        aria-label={isClickable ? `View photo of ${displayName}` : undefined}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }
            : undefined
        }
      >
        {/* Show image if we have a source and no thumb error */}
        {thumbPhotoSrc && !thumbError ? (
          <img
            src={thumbPhotoSrc}
            alt={alt || displayName}
            style={avatarImage.default}
            onError={() => setThumbError(true)}
          />
        ) : (
          /* Show initials if no photo OR thumbnail failed to load */
          <span style={{ ...initialsStyle, color: 'var(--text-secondary)' }}>{initials}</span>
        )}
      </div>

      {/* Built-in Photo Modal */}
      {enablePhotoModal && fullPhotoSrc && !fullError && (
        <Modal
          isOpen={photoModalOpen}
          onClose={() => setPhotoModalOpen(false)}
          title={displayName}
          maxWidth="600px"
          zIndex={10000}
          ariaLabel={`Photo of ${displayName}`}
        >
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <img
              src={fullPhotoSrc}
              alt={displayName}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 'var(--radius)',
              }}
              onError={() => setFullError(true)}
            />
          </div>
        </Modal>
      )}

      {/* Modal fallback when full photo failed */}
      {enablePhotoModal && fullPhotoSrc && fullError && (
        <Modal
          isOpen={photoModalOpen}
          onClose={() => setPhotoModalOpen(false)}
          title={displayName}
          maxWidth="600px"
          zIndex={10000}
          ariaLabel={`Photo of ${displayName}`}
        >
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            Photo unavailable
          </div>
        </Modal>
      )}
    </>
  );
}
